import { DoctorDocument } from "./../../services/doctor_document";

const DOCTOR_REQUIRED_DOCUMENT_TYPES = [
  "identity_or_cnh",
  "cpf",
  "diploma",
  "rqe",
  "curriculum",
  "vaccination_card",
  "training_certificates",
  "crm",
  "annual_fee_certificate",
  "address_proof",
  "photo",
  "mini_curriculum",
  "specialty_title",
  "ethical_certificate",
  "registration_regularity_certificate"
];

const HEALTH_PROFESSIONAL_REQUIRED_DOCUMENT_TYPES = [
  "identity_or_cnh",
  "cpf",
  "diploma",
  "curriculum",
  "vaccination_card",
  "training_certificates",
  "class_registration_card",
  "class_annual_fee_certificate",
  "address_proof",
  "photo",
  "mini_curriculum",
  "ethical_certificate",
  "registration_regularity_certificate"
];

const HEALTH_PROFESSIONAL_OPTIONAL_DOCUMENT_TYPES = ["specialization_titles"];

function resolveRequiredDocumentTypes(doctorProperty) {
  if (!doctorProperty) return [];

  if (
    doctorProperty.required_document_types &&
    doctorProperty.required_document_types.length
  ) {
    return doctorProperty.required_document_types;
  }

  const councilName =
    doctorProperty.council_name ||
    (doctorProperty.council && doctorProperty.council.name) ||
    "";

  if (councilName.startsWith("CRM")) {
    return DOCTOR_REQUIRED_DOCUMENT_TYPES;
  }

  if (councilName) {
    return HEALTH_PROFESSIONAL_REQUIRED_DOCUMENT_TYPES;
  }

  return [];
}

function resolveOptionalDocumentTypes(requiredDocumentTypes) {
  if (
    requiredDocumentTypes.length === 0 ||
    requiredDocumentTypes.includes("crm")
  ) {
    return [];
  }

  return HEALTH_PROFESSIONAL_OPTIONAL_DOCUMENT_TYPES;
}

export const state = {
  documents: [],
  loading: false,
  requiredDocumentTypes: [],
  optionalDocumentTypes: []
};

export const mutations = {
  SET_DOCUMENTS(state, payload) {
    state.documents = payload;
  },
  ADD_DOCUMENT(state, payload) {
    state.documents.push(payload);
  },
  UPDATE_DOCUMENT(state, payload) {
    const index = state.documents.findIndex(doc => doc.id === payload.id);
    if (index !== -1) {
      state.documents.splice(index, 1, payload);
    }
  },
  REMOVE_DOCUMENT(state, id) {
    state.documents = state.documents.filter(doc => doc.id !== id);
  },
  SET_LOADING(state, payload) {
    state.loading = payload;
  },
  SET_DOCUMENT_REQUIREMENTS(
    state,
    { requiredDocumentTypes, optionalDocumentTypes }
  ) {
    state.requiredDocumentTypes = requiredDocumentTypes;
    state.optionalDocumentTypes = optionalDocumentTypes;
  }
};

export const getters = {
  documents(state) {
    return state.documents;
  },
  requiredDocumentTypes(state) {
    return state.requiredDocumentTypes;
  },
  optionalDocumentTypes(state) {
    return state.optionalDocumentTypes;
  },
  allDocumentsSent(state) {
    if (state.requiredDocumentTypes.length === 0) return false;

    const sentTypes = state.documents.map(doc => doc.document_type);
    return state.requiredDocumentTypes.every(type => sentTypes.includes(type));
  },
  allDocumentsApproved(state) {
    if (state.requiredDocumentTypes.length === 0) return false;

    const sentTypes = state.documents.map(doc => doc.document_type);
    if (!state.requiredDocumentTypes.every(type => sentTypes.includes(type)))
      return false;

    return state.documents
      .filter(doc => state.requiredDocumentTypes.includes(doc.document_type))
      .every(doc => doc.status !== "in_analysis" && doc.status !== "rejected");
  },
  hasDocumentsInAnalysis(state) {
    return state.documents
      .filter(doc => state.requiredDocumentTypes.includes(doc.document_type))
      .some(doc => doc.status === "in_analysis");
  },
  hasDocumentsRejected(state) {
    return state.documents
      .filter(doc => state.requiredDocumentTypes.includes(doc.document_type))
      .some(doc => doc.status === "rejected");
  },
  sentDocumentsCount(state) {
    const sentTypes = state.documents.map(doc => doc.document_type);
    return state.requiredDocumentTypes.filter(type => sentTypes.includes(type))
      .length;
  },
  totalDocumentsRequired(state) {
    return state.requiredDocumentTypes.length;
  },
  loading(state) {
    return state.loading;
  }
};

export const actions = {
  syncDocumentRequirements({ commit }, doctorProperty) {
    const requiredDocumentTypes = resolveRequiredDocumentTypes(doctorProperty);
    const optionalDocumentTypes = resolveOptionalDocumentTypes(
      requiredDocumentTypes
    );

    commit("SET_DOCUMENT_REQUIREMENTS", {
      requiredDocumentTypes,
      optionalDocumentTypes
    });
  },

  fetchDocuments({ commit }) {
    commit("SET_LOADING", true);
    return DoctorDocument.fetchAll()
      .then(response => {
        commit("SET_DOCUMENTS", response.data);
        return response;
      })
      .catch(error => error)
      .finally(() => {
        commit("SET_LOADING", false);
      });
  },

  showDocument(_context, id) {
    return DoctorDocument.show(id);
  },

  createDocument({ commit }, payload) {
    commit("SET_LOADING", true);
    return DoctorDocument.create(payload)
      .then(response => {
        commit("ADD_DOCUMENT", response.data);
        return response;
      })
      .finally(() => {
        commit("SET_LOADING", false);
      });
  },

  updateDocument({ commit }, { id, doctorDocument }) {
    commit("SET_LOADING", true);
    return DoctorDocument.update(id, doctorDocument)
      .then(() => DoctorDocument.show(id))
      .then(response => {
        commit("UPDATE_DOCUMENT", response.data);
        return response;
      })
      .finally(() => {
        commit("SET_LOADING", false);
      });
  },

  deleteDocument({ commit }, id) {
    commit("SET_LOADING", true);
    return DoctorDocument.destroy(id)
      .then(response => {
        commit("REMOVE_DOCUMENT", id);
        return response;
      })
      .finally(() => {
        commit("SET_LOADING", false);
      });
  }
};
